import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams } from "react-router-dom";
import { translations } from "./translations";
import { LoadScript } from "@react-google-maps/api";
import PlaceAutocomplete from "./components/PlaceAutocomplete";
import { 
  PhoneIcon, LocationIcon, CalendarIcon, ClockIcon, UsersIcon,
  CreditCardIcon, CashIcon, CarIcon, LuggageIcon, CheckIcon,
  AlertIcon, MailIcon, NoteIcon, MoneyIcon, LoadingIcon
} from "./Icons";
import LanguageModal from "./components/LanguageModal";
import Header from "./components/Header";
import SimpleAdminDashboard from "./SimpleAdminDashboard";

// Lazy load pages
const ThankYouPage = lazy(() => import("./ThankYouPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const WhyUsPage = lazy(() => import("./pages/WhyUsPage"));
const DestinationsPage = lazy(() => import("./pages/DestinationsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AirportsPage = lazy(() => import("./pages/AirportsPage"));

// Smart API URL detection - ALWAYS uses dynamic detection  
const getApiUrl = () => {
  // In development (localhost:3000), use localhost:8001
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:8001';
  }
  
  // In production/preview, use same origin (Kubernetes ingress handles /api routing)
  // This works for both preview and deployment automatically
  return typeof window !== 'undefined' ? window.location.origin : '';
};

const API_URL = getApiUrl();
console.log('🔍 App.js API_URL:', API_URL);
const libraries = ["places"];

const ReservationModal = ({ isOpen, onClose, lang, startWithForm = false }) => {
  const navigate = useNavigate();
  const t = translations[lang] || translations.fr;
  const [showForm, setShowForm] = useState(startWithForm);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phonePrefix: '+32',
    phone: '',
    pickup: '',
    destination: '',
    date: '',
    time: '',
    bookingType: 'immediate', // 'immediate' or 'scheduled'
    passengers: '1',
    vehicleType: 'eco',
    luggage: '0',
    hasBaggage: false,
    paymentMethod: 'cash', // 'cash' or 'card'
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [sessionId] = useState(() => 'sess_' + Math.random().toString(36).substr(2, 9));

  const trackAttempt = async (type, status = 'in_progress', extraData = {}) => {
    try {
      await fetch(`${API_URL}/api/track-attempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          attempt_type: type,
          status: status,
          ...formData,
          ...extraData,
          price: priceInfo?.price,
          distance_km: priceInfo?.distance_km,
          duration_min: priceInfo?.duration_min
        })
      });
    } catch (e) {
      console.error("Tracking error", e);
    }
  };
  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    console.log(`handleInputChange: ${field} = ${value}`);
    setFormData(prev => ({ ...prev, [field]: value }));
    // Reset price when relevant fields change
    if (['pickup', 'destination', 'date', 'time', 'vehicleType', 'hasBaggage'].includes(field)) {
      setPriceInfo(null);
    }
  };

  const calculatePrice = async () => {
    // Validate required fields
    if (!formData.pickup || !formData.destination) {
      alert('Veuillez remplir les adresses pour calculer le prix.');
      return;
    }

    // For immediate booking, use current time
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().split(' ')[0].substring(0, 5);

    const dateToUse = formData.bookingType === 'immediate' ? currentDate : (formData.date || currentDate);
    const timeToUse = formData.bookingType === 'immediate' ? currentTime : (formData.time || currentTime);

    setIsCalculatingPrice(true);
    try {
      const response = await fetch(`${API_URL}/api/calculate-price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pickup: formData.pickup,
          destination: formData.destination,
          date: dateToUse,
          time: timeToUse,
          vehicleType: formData.vehicleType,
          hasBaggage: formData.hasBaggage
        })
      });

      if (response.ok) {
        const result = await response.json();
        setPriceInfo(result);
        trackAttempt('price_calculation', 'success', result);
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors du calcul du prix');
      }
    } catch (error) {
      console.error('Error calculating price:', error);
      alert('Erreur de connexion lors du calcul du prix');
    } finally {
      setIsCalculatingPrice(false);
    }
  };

  const handleSubmitReservation = async (e) => {
    e.preventDefault();
    
    // Prevent submission if price hasn't been calculated
    if (!priceInfo || !priceInfo.price) {
      alert(lang === 'fr' ? 'Veuillez d\'abord calculer le prix avant de confirmer.' : 
            lang === 'nl' ? 'Bereken eerst de prijs voordat u bevestigt.' :
            'Please calculate the price before confirming.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Include the calculated price in the submission
      const submissionData = {
        ...formData,
        calculatedPrice: priceInfo.price,
        distance_km: priceInfo.distance_km || null,
        duration_min: priceInfo.duration_min || null
      };
      
      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        const result = await response.json();
        trackAttempt('form_submission', 'success', { booking_id: result.id });
        setSubmitStatus('success');
        
        // Close modal
        onClose();
        
        // Prepare booking data for thank you page
        const bookingData = {
          id: result.id,
          name: result.name,
          email: result.email,
          phone: result.phone,
          pickup: result.pickup,
          destination: result.destination,
          date: result.date,
          time: result.time,
          passengers: result.passengers,
          luggage: result.luggage,
          vehicleType: result.vehicleType,
          paymentMethod: result.paymentMethod,
          price: result.price,
          distance_km: result.distance_km,
          duration_min: result.duration_min
        };
        
        // Redirect to thank you page based on language
        const thankYouUrl = lang === 'fr' ? '/fr/merci' : lang === 'nl' ? '/nl/bedankt' : '/en/thank-you';
        navigate(thankYouUrl, { state: { bookingData } });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phonePrefix: '+32',
          phone: '',
          pickup: '',
          destination: '',
          date: '',
          time: '',
          bookingType: 'immediate',
          passengers: '1',
          vehicleType: 'eco',
          luggage: '0',
          hasBaggage: false,
          paymentMethod: 'cash',
          notes: ''
        });
        setPriceInfo(null);
        setShowForm(false);
      } else {
        const error = await response.json();
        trackAttempt('form_submission', 'failed', { error_message: error.detail || 'API Error' });
        setSubmitStatus('error');
        alert('Erreur lors de la réservation. Veuillez réessayer ou nous appeler au 02 844 54 87.');
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      trackAttempt('form_submission', 'failed', { error_message: error.message });
      setSubmitStatus('error');
      alert('Erreur de connexion. Veuillez vérifier votre connexion internet ou nous appeler au 02 844 54 87.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reservationT = {
    fr: {
      title: 'Comment souhaitez-vous réserver ?',
      callOption: 'Appeler Maintenant',
      callDesc: 'Parlez directement avec notre équipe',
      onlineOption: 'Réserver en Ligne',
      onlineDesc: 'Remplissez le formulaire de réservation',
      formTitle: 'Formulaire de Réservation',
      namePlaceholder: 'Votre nom complet',
      emailPlaceholder: 'Votre adresse email',
      phonePlaceholder: 'Votre numéro de téléphone',
      pickupPlaceholder: 'Adresse de prise en charge',
      destPlaceholder: 'Adresse de destination',
      datePlaceholder: 'Date de réservation',
      timePlaceholder: 'Heure de prise en charge',
      passengersLabel: 'Nombre de passagers',
      vehicleTypeLabel: 'Type de véhicule',
      luggageLabel: 'Nombre de bagages',
      notesPlaceholder: 'Notes supplémentaires (optionnel)',
      submitButton: 'Confirmer la Réservation',
      backButton: 'Retour',
      vehicles: {
        eco: 'Eco - Véhicule standard',
        business: 'Business - Véhicule premium',
        van: 'Van - 7-8 places',
        prestige: 'Prestige - Véhicule de luxe',
        pmr: 'PMR - Adapté handicap'
      },
      passengerSingular: 'passager',
      passengerPlural: 'passagers',
      luggageSingular: 'bagage',
      luggagePlural: 'bagages',
      extraLuggageLabel: 'Supplément bagages',
      paymentModeLabel: 'Mode de paiement (au chauffeur)',
      paymentCash: 'Espèces',
      paymentCard: 'Carte bancaire',
      calculatePriceButton: 'Calculer le Prix',
      calculatingPrice: 'Calcul en cours...',
      estimatedPrice: 'Prix estimé',
      submitting: 'Envoi en cours...',
      confirmButton: 'Confirmer',
      confirmationTitle: 'Réservation confirmée',
      confirmationSubtitle: 'Votre réservation a été enregistrée avec succès.',
      bookingNumber: 'Numéro de réservation',
      rideDetails: 'Détails de la Course',
      pickup: 'Prise en charge:',
      destination: 'Destination:',
      date: 'Date:',
      time: 'Heure:',
      informations: 'Informations',
      passengers: 'Passagers:',
      vehicle: 'Véhicule:',
      payment: 'Paiement:',
      estimatedFare: 'Tarif estimé',
      needHelp: 'Besoin d\'aide ?',
      phone: 'Téléphone:',
      email: 'Email:',
      driverOnTime: 'Notre chauffeur sera à l\'heure au point de rendez-vous.',
      telegramNotification: 'Vous recevrez bientôt nos informations par Telegram.',
      closeButton: 'Fermer',
      rideTypeLabel: 'Type de course',
      immediate: 'Immédiate',
      reservation: 'Réservation'
    },
    nl: {
      title: 'Hoe wilt u reserveren?',
      callOption: 'Nu Bellen',
      callDesc: 'Praat rechtstreeks met ons team',
      onlineOption: 'Online Reserveren',
      onlineDesc: 'Vul het reserveringsformulier in',
      formTitle: 'Reserveringsformulier',
      namePlaceholder: 'Uw volledige naam',
      emailPlaceholder: 'Uw e-mailadres',
      phonePlaceholder: 'Uw telefoonnummer',
      pickupPlaceholder: 'Ophaaladres',
      destPlaceholder: 'Bestemmingsadres',
      datePlaceholder: 'Reserveringsdatum',
      timePlaceholder: 'Ophaaltijd',
      passengersLabel: 'Aantal passagiers',
      vehicleTypeLabel: 'Type voertuig',
      luggageLabel: 'Aantal bagage',
      notesPlaceholder: 'Extra opmerkingen (optioneel)',
      submitButton: 'Reservering Bevestigen',
      backButton: 'Terug',
      vehicles: {
        eco: 'Eco - Standaard voertuig',
        business: 'Business - Premium voertuig',
        van: 'Van - 7-8 plaatsen',
        prestige: 'Prestige - Luxe voertuig',
        pmr: 'PMR - Aangepast gehandicapten'
      },
      passengerSingular: 'passagier',
      passengerPlural: 'passagiers',
      luggageSingular: 'bagage',
      luggagePlural: 'bagages',
      extraLuggageLabel: 'Bagage supplement',
      paymentModeLabel: 'Betaalwijze (aan chauffeur)',
      paymentCash: 'Contant',
      paymentCard: 'Bankkaart',
      calculatePriceButton: 'Prijs Berekenen',
      calculatingPrice: 'Berekening bezig...',
      estimatedPrice: 'Geschatte prijs',
      submitting: 'Verzenden...',
      confirmButton: 'Bevestigen',
      confirmationTitle: 'Reservering bevestigd',
      confirmationSubtitle: 'Uw reservering is succesvol geregistreerd.',
      bookingNumber: 'Reserveringsnummer',
      rideDetails: 'Rit Details',
      pickup: 'Ophalen:',
      destination: 'Bestemming:',
      date: 'Datum:',
      time: 'Tijd:',
      informations: 'Informatie',
      passengers: 'Passagiers:',
      vehicle: 'Voertuig:',
      payment: 'Betaling:',
      estimatedFare: 'Geschat tarief',
      needHelp: 'Hulp nodig?',
      phone: 'Telefoon:',
      email: 'E-mail:',
      driverOnTime: 'Onze chauffeur zal op tijd zijn op de afspraakplaats.',
      telegramNotification: 'U ontvangt binnenkort onze informatie via Telegram.',
      closeButton: 'Sluiten',
      rideTypeLabel: 'Type van rit',
      immediate: 'Onmiddellijk',
      reservation: 'Reservering'
    },
    en: {
      title: 'How would you like to book?',
      callOption: 'Call Now',
      callDesc: 'Speak directly with our team',
      onlineOption: 'Book Online',
      onlineDesc: 'Fill out the reservation form',
      formTitle: 'Reservation Form',
      namePlaceholder: 'Your full name',
      emailPlaceholder: 'Your email address',
      phonePlaceholder: 'Your phone number',
      pickupPlaceholder: 'Pickup address',
      destPlaceholder: 'Destination address',
      datePlaceholder: 'Reservation date',
      timePlaceholder: 'Pickup time',
      passengersLabel: 'Number of passengers',
      vehicleTypeLabel: 'Vehicle type',
      luggageLabel: 'Number of luggage',
      notesPlaceholder: 'Additional notes (optional)',
      submitButton: 'Confirm Reservation',
      backButton: 'Back',
      vehicles: {
        eco: 'Eco - Standard vehicle',
        business: 'Business - Premium vehicle',
        van: 'Van - 7-8 seats',
        prestige: 'Prestige - Luxury vehicle',
        pmr: 'PMR - Wheelchair accessible'
      },
      passengerSingular: 'passenger',
      passengerPlural: 'passengers',
      luggageSingular: 'luggage',
      luggagePlural: 'luggage',
      extraLuggageLabel: 'Extra luggage',
      paymentModeLabel: 'Payment method (to driver)',
      paymentCash: 'Cash',
      paymentCard: 'Credit card',
      calculatePriceButton: 'Calculate Price',
      calculatingPrice: 'Calculating...',
      estimatedPrice: 'Estimated price',
      submitting: 'Submitting...',
      confirmButton: 'Confirm',
      confirmationTitle: 'Booking confirmed',
      confirmationSubtitle: 'Your booking has been successfully registered.',
      bookingNumber: 'Booking number',
      rideDetails: 'Ride Details',
      pickup: 'Pickup:',
      destination: 'Destination:',
      date: 'Date:',
      time: 'Time:',
      informations: 'Information',
      passengers: 'Passengers:',
      vehicle: 'Vehicle:',
      payment: 'Payment:',
      estimatedFare: 'Estimated fare',
      needHelp: 'Need help?',
      phone: 'Phone:',
      email: 'Email:',
      driverOnTime: 'Our driver will be on time at the meeting point.',
      telegramNotification: 'You will soon receive our information via Telegram.',
      closeButton: 'Close',
      rideTypeLabel: 'Ride type',
      immediate: 'Immediate',
      reservation: 'Reservation'
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
    onClose();
  };

  const rt = reservationT[lang] || reservationT.fr;
  const heroT = t.hero || translations.fr.hero;

  // Confirmation Modal
  if (showConfirmation && confirmationData) {
    const paymentText = confirmationData.paymentMethod === 'cash' ? rt.paymentCash : rt.paymentCard;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
          {/* Success Icon & Title */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckIcon className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{rt.confirmationTitle}</h2>
            <p className="text-gray-600">{rt.confirmationSubtitle}</p>
          </div>

          {/* Booking Reference */}
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-6 text-center">
            <p className="text-sm text-gray-600 mb-1">{rt.bookingNumber}</p>
            <p className="text-xl font-mono font-bold text-gray-900">{confirmationData.bookingId.split('-')[0].toUpperCase()}</p>
          </div>

          {/* Booking Details */}
          <div className="space-y-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <LocationIcon className="w-5 h-5" /> {rt.rideDetails}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{rt.pickup}</span>
                  <span className="text-gray-900 font-medium text-right ml-2">{confirmationData.pickup}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{rt.destination}</span>
                  <span className="text-gray-900 font-medium text-right ml-2">{confirmationData.destination}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{rt.date}</span>
                  <span className="text-gray-900 font-medium">{confirmationData.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{rt.time}</span>
                  <span className="text-gray-900 font-medium">{confirmationData.time}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CarIcon className="w-5 h-5" /> {rt.informations}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{rt.passengers}</span>
                  <span className="text-gray-900 font-medium">{confirmationData.passengers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{rt.vehicle}</span>
                  <span className="text-gray-900 font-medium">{confirmationData.vehicleType.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{rt.payment}</span>
                  <span className="text-gray-900 font-medium">{paymentText}</span>
                </div>
              </div>
            </div>

            {/* Price */}
            {confirmationData.price && confirmationData.price > 0 && (
              <div className="bg-green-50 border-2 border-green-400 rounded-lg p-6 text-center">
                <p className="text-gray-600 text-sm mb-1">{rt.estimatedFare}</p>
                <p className="text-4xl font-bold text-green-600">{Math.round(confirmationData.price)}€</p>
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <PhoneIcon className="w-5 h-5" /> {rt.needHelp}
            </h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>{rt.phone}</strong> 02 844 54 87</p>
              <p><strong>{rt.email}</strong> info@brutaxi-bel.be</p>
            </div>
          </div>

          {/* Additional Info */}
          <div className="text-center text-sm text-gray-600 mb-6">
            <p>{rt.driverOnTime}</p>
            <p className="mt-2">{rt.telegramNotification}</p>
          </div>

          {/* Close Button */}
          <button
            onClick={handleCloseConfirmation}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 rounded-lg transition-colors text-lg"
          >
            {rt.closeButton}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <div className={`rounded-none sm:rounded-2xl max-w-2xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto ${showForm ? 'relative' : 'bg-gray-900'}`} onClick={(e) => e.stopPropagation()}>
        {!showForm ? (
          <div className="p-6 sm:p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white">{rt.title}</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl" data-testid="close-modal-button">
                ✖
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* Option 1: Appeler */}
              <a
                href="tel:+3228445487"
                className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-6 text-center hover:scale-105 transition-transform group"
                data-testid="call-option"
              >
                <div className="flex justify-center mb-4">
                  <PhoneIcon className="w-16 h-16 text-black" />
                </div>
                <h3 className="text-xl font-bold text-black mb-2">{rt.callOption}</h3>
                <p className="text-gray-900 mb-4">{rt.callDesc}</p>
                <p className="text-2xl font-bold text-black">02 844 54 87</p>
              </a>

              {/* Option 2: Réserver en ligne */}
              <button
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-br from-gray-800 to-gray-700 border-2 border-yellow-500 rounded-xl p-6 text-center hover:scale-105 transition-transform"
                data-testid="online-option"
              >
                <div className="flex justify-center mb-4">
                  <NoteIcon className="w-16 h-16 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{rt.onlineOption}</h3>
                <p className="text-gray-300">{rt.onlineDesc}</p>
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Background image for form */}
            <div className="absolute inset-0 bg-cover bg-center opacity-30 rounded-2xl" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_site-fixes-2/artifacts/6i17gngc_ChatGPT%20Image%201%20aou%CC%82t%202025%2C%2020_20_33.png')"}}></div>
            <div className="absolute inset-0 bg-gradient-to-br from-black via-black/90 to-black/80 rounded-2xl"></div>
            <div className="relative z-10 p-6 sm:p-8">
            <div className="flex justify-between items-center mb-6">
              <button onClick={() => setShowForm(false)} className="text-yellow-500 hover:text-yellow-400 flex items-center" data-testid="back-button">
                ← {rt.backButton}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">
                ✖
              </button>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center">{rt.formTitle}</h2>

            <form onSubmit={handleSubmitReservation} className="space-y-4 pb-20 sm:pb-0">
              {/* 1. Adresses (Pickup/Destination) */}
              <div>
                <label className="block text-white text-sm font-bold mb-2">
                  {rt.pickupPlaceholder}
                </label>
                <PlaceAutocomplete
                  value={formData.pickup}
                  onChange={(value) => handleInputChange('pickup', value)}
                  placeholder={rt.pickupPlaceholder}
                  className="w-full px-4 py-4 rounded-lg bg-white text-black placeholder-gray-500 border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base font-medium"
                  testId="reservation-pickup-input"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-bold mb-2">
                  {rt.destPlaceholder}
                </label>
                <PlaceAutocomplete
                  value={formData.destination}
                  onChange={(value) => handleInputChange('destination', value)}
                  placeholder={rt.destPlaceholder}
                  className="w-full px-4 py-4 rounded-lg bg-white text-black placeholder-gray-500 border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base font-medium"
                  testId="reservation-destination-input"
                />
              </div>

              {/* 2. Type de course (Immédiate/Réservation) */}
              <div className="bg-yellow-500 bg-opacity-20 border-2 border-yellow-500 rounded-lg p-4">
                <label className="block text-white text-sm font-semibold mb-3 flex items-center gap-2">
                  <ClockIcon className="w-5 h-5" /> {rt.rideTypeLabel}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('bookingType', 'immediate')}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      formData.bookingType === 'immediate'
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white text-black hover:bg-yellow-100'
                    }`}
                  >
                    <ClockIcon className="w-5 h-5" /> {rt.immediate}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('bookingType', 'scheduled')}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      formData.bookingType === 'scheduled'
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white text-black hover:bg-yellow-100'
                    }`}
                  >
                    <CalendarIcon className="w-5 h-5" /> {rt.reservation}
                  </button>
                </div>
              </div>

              {/* 3. Date et Heure */}
              {formData.bookingType === 'scheduled' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-bold mb-1">{rt.datePlaceholder}</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-white text-black border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base appearance-none min-h-[50px]"
                      required
                      data-testid="reservation-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-bold mb-1">{rt.timePlaceholder}</label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => handleInputChange('time', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-white text-black border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base appearance-none min-h-[50px]"
                      required
                      data-testid="reservation-time-input"
                    />
                  </div>
                </div>
              )}

              {/* 4. Détails (Passagers, Bagages) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white font-semibold mb-2">{rt.passengersLabel}</label>
                  <select
                    value={formData.passengers}
                    onChange={(e) => handleInputChange('passengers', e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white text-black border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base min-h-[50px]"
                    required
                    data-testid="reservation-passengers-select"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                      <option key={num} value={num}>{num} {num === 1 ? rt.passengerSingular : rt.passengerPlural}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-white font-semibold mb-2">{rt.luggageLabel}</label>
                  <select
                    value={formData.luggage}
                    onChange={(e) => handleInputChange('luggage', e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white text-black border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base min-h-[50px]"
                    required
                    data-testid="reservation-luggage-select"
                  >
                    {[0, 1, 2, 3, 4, 5, 6].map(num => (
                      <option key={num} value={num}>{num} {num <= 1 ? rt.luggageSingular : rt.luggagePlural}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 5. Véhicule */}
              <div>
                <label className="block text-sm text-white font-semibold mb-2">{rt.vehicleTypeLabel}</label>
                <select
                  value={formData.vehicleType}
                  onChange={(e) => handleInputChange('vehicleType', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-white text-black border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base min-h-[50px]"
                  required
                  data-testid="reservation-vehicle-select"
                >
                  <option value="eco">{rt.vehicles.eco}</option>
                  <option value="business">{rt.vehicles.business}</option>
                  <option value="van">{rt.vehicles.van}</option>
                  <option value="prestige">{rt.vehicles.prestige}</option>
                  <option value="pmr">{rt.vehicles.pmr}</option>
                </select>
              </div>

              {/* 6. Mode de paiement (MOVED UP) */}
              <div className="bg-blue-500 bg-opacity-20 border-2 border-blue-500 rounded-lg p-4">
                <label className="block text-white text-sm font-semibold mb-3 flex items-center gap-2">
                  <CreditCardIcon className="w-5 h-5" /> {rt.paymentModeLabel}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('paymentMethod', 'cash')}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      formData.paymentMethod === 'cash'
                        ? 'bg-blue-500 text-white'
                        : 'bg-white text-black hover:bg-blue-100'
                    }`}
                  >
                    <CashIcon className="w-5 h-5" /> {rt.paymentCash}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('paymentMethod', 'card')}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      formData.paymentMethod === 'card'
                        ? 'bg-blue-500 text-white'
                        : 'bg-white text-black hover:bg-blue-100'
                    }`}
                  >
                    <CreditCardIcon className="w-5 h-5" /> {rt.paymentCard}
                  </button>
                </div>
              </div>

              {/* 7. Bouton calcul prix (MOVED UP) */}
              <button
                type="button"
                onClick={calculatePrice}
                disabled={isCalculatingPrice}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCalculatingPrice ? (
                  <>
                    <LoadingIcon className="w-5 h-5" /> {rt.calculatingPrice}
                  </>
                ) : (
                  <>
                    <MoneyIcon className="w-5 h-5" /> {rt.calculatePriceButton}
                  </>
                )}
              </button>

              {/* Affichage du prix (MOVED UP) */}
              {priceInfo && (
                <div className="bg-green-900 bg-opacity-30 border-2 border-green-500 rounded-lg p-4">
                  <div className="text-center">
                    <p className="text-gray-300 text-sm mb-1">{rt.estimatedPrice}</p>
                    <p className="text-3xl font-bold text-green-400">{Math.round(priceInfo.price)}€</p>
                    <p className="text-gray-400 text-xs mt-2">
                      {priceInfo.distance_text} • {priceInfo.duration_text}
                    </p>
                  </div>
                </div>
              )}
              
              {/* 8. Informations Personnelles (Moved Down) */}
              <div className="bg-gray-800 bg-opacity-50 rounded-lg p-4 border border-gray-700">
                <h3 className="text-white font-semibold mb-3">Vos Coordonnées</h3>
                <p className="text-gray-400 text-xs mb-3">
                  <span className="text-red-500">*</span> Champs obligatoires
                </p>
                <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-white text-sm mb-1">
                        Nom <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder={rt.namePlaceholder}
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-4 py-3 rounded-lg bg-white text-black placeholder-gray-500 border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base"
                        required
                        data-testid="reservation-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white text-sm mb-1">
                        E-mail <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        placeholder={rt.emailPlaceholder}
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full px-4 py-3 rounded-lg bg-white text-black placeholder-gray-500 border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base"
                        required
                        data-testid="reservation-email-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-white text-sm mb-1">
                        Téléphone <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={formData.phonePrefix || '+32'}
                          onChange={(e) => handleInputChange('phonePrefix', e.target.value)}
                          className="w-32 px-3 py-3 rounded-lg bg-white text-black border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
                        >
                          <option value="+93">🇦🇫 Afghanistan +93</option>
                          <option value="+355">🇦🇱 Albanie +355</option>
                          <option value="+213">🇩🇿 Algérie +213</option>
                          <option value="+376">🇦🇩 Andorre +376</option>
                          <option value="+244">🇦🇴 Angola +244</option>
                          <option value="+1264">🇦🇮 Anguilla +1264</option>
                          <option value="+1268">🇦🇬 Antigua-et-Barbuda +1268</option>
                          <option value="+54">🇦🇷 Argentine +54</option>
                          <option value="+374">🇦🇲 Arménie +374</option>
                          <option value="+297">🇦🇼 Aruba +297</option>
                          <option value="+61">🇦🇺 Australie +61</option>
                          <option value="+43">🇦🇹 Autriche +43</option>
                          <option value="+994">🇦🇿 Azerbaïdjan +994</option>
                          <option value="+1242">🇧🇸 Bahamas +1242</option>
                          <option value="+973">🇧🇭 Bahreïn +973</option>
                          <option value="+880">🇧🇩 Bangladesh +880</option>
                          <option value="+1246">🇧🇧 Barbade +1246</option>
                          <option value="+375">🇧🇾 Biélorussie +375</option>
                          <option value="+32">🇧🇪 Belgique +32</option>
                          <option value="+501">🇧🇿 Belize +501</option>
                          <option value="+229">🇧🇯 Bénin +229</option>
                          <option value="+1441">🇧🇲 Bermudes +1441</option>
                          <option value="+975">🇧🇹 Bhoutan +975</option>
                          <option value="+591">🇧🇴 Bolivie +591</option>
                          <option value="+387">🇧🇦 Bosnie-Herzégovine +387</option>
                          <option value="+267">🇧🇼 Botswana +267</option>
                          <option value="+55">🇧🇷 Brésil +55</option>
                          <option value="+673">🇧🇳 Brunei +673</option>
                          <option value="+359">🇧🇬 Bulgarie +359</option>
                          <option value="+226">🇧🇫 Burkina Faso +226</option>
                          <option value="+257">🇧🇮 Burundi +257</option>
                          <option value="+855">🇰🇭 Cambodge +855</option>
                          <option value="+237">🇨🇲 Cameroun +237</option>
                          <option value="+1">🇨🇦 Canada +1</option>
                          <option value="+238">🇨🇻 Cap-Vert +238</option>
                          <option value="+1345">🇰🇾 Îles Caïmans +1345</option>
                          <option value="+236">🇨🇫 Rép. Centrafricaine +236</option>
                          <option value="+235">🇹🇩 Tchad +235</option>
                          <option value="+56">🇨🇱 Chili +56</option>
                          <option value="+86">🇨🇳 Chine +86</option>
                          <option value="+57">🇨🇴 Colombie +57</option>
                          <option value="+269">🇰🇲 Comores +269</option>
                          <option value="+242">🇨🇬 Congo +242</option>
                          <option value="+243">🇨🇩 Congo (RDC) +243</option>
                          <option value="+682">🇨🇰 Îles Cook +682</option>
                          <option value="+506">🇨🇷 Costa Rica +506</option>
                          <option value="+225">🇨🇮 Côte d&apos;Ivoire +225</option>
                          <option value="+385">🇭🇷 Croatie +385</option>
                          <option value="+53">🇨🇺 Cuba +53</option>
                          <option value="+357">🇨🇾 Chypre +357</option>
                          <option value="+420">🇨🇿 Tchéquie +420</option>
                          <option value="+45">🇩🇰 Danemark +45</option>
                          <option value="+253">🇩🇯 Djibouti +253</option>
                          <option value="+1767">🇩🇲 Dominique +1767</option>
                          <option value="+1809">🇩🇴 Rép. Dominicaine +1809</option>
                          <option value="+593">🇪🇨 Équateur +593</option>
                          <option value="+20">🇪🇬 Égypte +20</option>
                          <option value="+503">🇸🇻 Salvador +503</option>
                          <option value="+240">🇬🇶 Guinée équatoriale +240</option>
                          <option value="+291">🇪🇷 Érythrée +291</option>
                          <option value="+372">🇪🇪 Estonie +372</option>
                          <option value="+251">🇪🇹 Éthiopie +251</option>
                          <option value="+500">🇫🇰 Îles Malouines +500</option>
                          <option value="+298">🇫🇴 Îles Féroé +298</option>
                          <option value="+679">🇫🇯 Fidji +679</option>
                          <option value="+358">🇫🇮 Finlande +358</option>
                          <option value="+33">🇫🇷 France +33</option>
                          <option value="+594">🇬🇫 Guyane française +594</option>
                          <option value="+689">🇵🇫 Polynésie française +689</option>
                          <option value="+241">🇬🇦 Gabon +241</option>
                          <option value="+220">🇬🇲 Gambie +220</option>
                          <option value="+995">🇬🇪 Géorgie +995</option>
                          <option value="+49">🇩🇪 Allemagne +49</option>
                          <option value="+233">🇬🇭 Ghana +233</option>
                          <option value="+350">🇬🇮 Gibraltar +350</option>
                          <option value="+30">🇬🇷 Grèce +30</option>
                          <option value="+299">🇬🇱 Groenland +299</option>
                          <option value="+1473">🇬🇩 Grenade +1473</option>
                          <option value="+590">🇬🇵 Guadeloupe +590</option>
                          <option value="+1671">🇬🇺 Guam +1671</option>
                          <option value="+502">🇬🇹 Guatemala +502</option>
                          <option value="+224">🇬🇳 Guinée +224</option>
                          <option value="+245">🇬🇼 Guinée-Bissau +245</option>
                          <option value="+592">🇬🇾 Guyana +592</option>
                          <option value="+509">🇭🇹 Haïti +509</option>
                          <option value="+504">🇭🇳 Honduras +504</option>
                          <option value="+852">🇭🇰 Hong Kong +852</option>
                          <option value="+36">🇭🇺 Hongrie +36</option>
                          <option value="+354">🇮🇸 Islande +354</option>
                          <option value="+91">🇮🇳 Inde +91</option>
                          <option value="+62">🇮🇩 Indonésie +62</option>
                          <option value="+98">🇮🇷 Iran +98</option>
                          <option value="+964">🇮🇶 Irak +964</option>
                          <option value="+353">🇮🇪 Irlande +353</option>
                          <option value="+972">🇮🇱 Israël +972</option>
                          <option value="+39">🇮🇹 Italie +39</option>
                          <option value="+1876">🇯🇲 Jamaïque +1876</option>
                          <option value="+81">🇯🇵 Japon +81</option>
                          <option value="+962">🇯🇴 Jordanie +962</option>
                          <option value="+7">🇰🇿 Kazakhstan +7</option>
                          <option value="+254">🇰🇪 Kenya +254</option>
                          <option value="+686">🇰🇮 Kiribati +686</option>
                          <option value="+383">🇽🇰 Kosovo +383</option>
                          <option value="+965">🇰🇼 Koweït +965</option>
                          <option value="+996">🇰🇬 Kirghizistan +996</option>
                          <option value="+856">🇱🇦 Laos +856</option>
                          <option value="+371">🇱🇻 Lettonie +371</option>
                          <option value="+961">🇱🇧 Liban +961</option>
                          <option value="+266">🇱🇸 Lesotho +266</option>
                          <option value="+231">🇱🇷 Libéria +231</option>
                          <option value="+218">🇱🇾 Libye +218</option>
                          <option value="+423">🇱🇮 Liechtenstein +423</option>
                          <option value="+370">🇱🇹 Lituanie +370</option>
                          <option value="+352">🇱🇺 Luxembourg +352</option>
                          <option value="+853">🇲🇴 Macao +853</option>
                          <option value="+389">🇲🇰 Macédoine du Nord +389</option>
                          <option value="+261">🇲🇬 Madagascar +261</option>
                          <option value="+265">🇲🇼 Malawi +265</option>
                          <option value="+60">🇲🇾 Malaisie +60</option>
                          <option value="+960">🇲🇻 Maldives +960</option>
                          <option value="+223">🇲🇱 Mali +223</option>
                          <option value="+356">🇲🇹 Malte +356</option>
                          <option value="+692">🇲🇭 Îles Marshall +692</option>
                          <option value="+596">🇲🇶 Martinique +596</option>
                          <option value="+222">🇲🇷 Mauritanie +222</option>
                          <option value="+230">🇲🇺 Maurice +230</option>
                          <option value="+262">🇾🇹 Mayotte +262</option>
                          <option value="+52">🇲🇽 Mexique +52</option>
                          <option value="+691">🇫🇲 Micronésie +691</option>
                          <option value="+373">🇲🇩 Moldavie +373</option>
                          <option value="+377">🇲🇨 Monaco +377</option>
                          <option value="+976">🇲🇳 Mongolie +976</option>
                          <option value="+382">🇲🇪 Monténégro +382</option>
                          <option value="+1664">🇲🇸 Montserrat +1664</option>
                          <option value="+212">🇲🇦 Maroc +212</option>
                          <option value="+258">🇲🇿 Mozambique +258</option>
                          <option value="+95">🇲🇲 Myanmar +95</option>
                          <option value="+264">🇳🇦 Namibie +264</option>
                          <option value="+674">🇳🇷 Nauru +674</option>
                          <option value="+977">🇳🇵 Népal +977</option>
                          <option value="+31">🇳🇱 Pays-Bas +31</option>
                          <option value="+687">🇳🇨 Nouvelle-Calédonie +687</option>
                          <option value="+64">🇳🇿 Nouvelle-Zélande +64</option>
                          <option value="+505">🇳🇮 Nicaragua +505</option>
                          <option value="+227">🇳🇪 Niger +227</option>
                          <option value="+234">🇳🇬 Nigéria +234</option>
                          <option value="+850">🇰🇵 Corée du Nord +850</option>
                          <option value="+47">🇳🇴 Norvège +47</option>
                          <option value="+968">🇴🇲 Oman +968</option>
                          <option value="+92">🇵🇰 Pakistan +92</option>
                          <option value="+680">🇵🇼 Palaos +680</option>
                          <option value="+970">🇵🇸 Palestine +970</option>
                          <option value="+507">🇵🇦 Panama +507</option>
                          <option value="+675">🇵🇬 Papouasie-Nouvelle-Guinée +675</option>
                          <option value="+595">🇵🇾 Paraguay +595</option>
                          <option value="+51">🇵🇪 Pérou +51</option>
                          <option value="+63">🇵🇭 Philippines +63</option>
                          <option value="+48">🇵🇱 Pologne +48</option>
                          <option value="+351">🇵🇹 Portugal +351</option>
                          <option value="+1787">🇵🇷 Porto Rico +1787</option>
                          <option value="+974">🇶🇦 Qatar +974</option>
                          <option value="+262">🇷🇪 Réunion +262</option>
                          <option value="+40">🇷🇴 Roumanie +40</option>
                          <option value="+7">🇷🇺 Russie +7</option>
                          <option value="+250">🇷🇼 Rwanda +250</option>
                          <option value="+290">🇸🇭 Sainte-Hélène +290</option>
                          <option value="+1869">🇰🇳 Saint-Kitts-et-Nevis +1869</option>
                          <option value="+1758">🇱🇨 Sainte-Lucie +1758</option>
                          <option value="+508">🇵🇲 Saint-Pierre-et-Miquelon +508</option>
                          <option value="+1784">🇻🇨 Saint-Vincent +1784</option>
                          <option value="+685">🇼🇸 Samoa +685</option>
                          <option value="+378">🇸🇲 Saint-Marin +378</option>
                          <option value="+239">🇸🇹 Sao Tomé-et-Principe +239</option>
                          <option value="+966">🇸🇦 Arabie saoudite +966</option>
                          <option value="+221">🇸🇳 Sénégal +221</option>
                          <option value="+381">🇷🇸 Serbie +381</option>
                          <option value="+248">🇸🇨 Seychelles +248</option>
                          <option value="+232">🇸🇱 Sierra Leone +232</option>
                          <option value="+65">🇸🇬 Singapour +65</option>
                          <option value="+421">🇸🇰 Slovaquie +421</option>
                          <option value="+386">🇸🇮 Slovénie +386</option>
                          <option value="+677">🇸🇧 Îles Salomon +677</option>
                          <option value="+252">🇸🇴 Somalie +252</option>
                          <option value="+27">🇿🇦 Afrique du Sud +27</option>
                          <option value="+82">🇰🇷 Corée du Sud +82</option>
                          <option value="+211">🇸🇸 Soudan du Sud +211</option>
                          <option value="+34">🇪🇸 Espagne +34</option>
                          <option value="+94">🇱🇰 Sri Lanka +94</option>
                          <option value="+249">🇸🇩 Soudan +249</option>
                          <option value="+597">🇸🇷 Suriname +597</option>
                          <option value="+268">🇸🇿 Eswatini +268</option>
                          <option value="+46">🇸🇪 Suède +46</option>
                          <option value="+41">🇨🇭 Suisse +41</option>
                          <option value="+963">🇸🇾 Syrie +963</option>
                          <option value="+886">🇹🇼 Taïwan +886</option>
                          <option value="+992">🇹🇯 Tadjikistan +992</option>
                          <option value="+255">🇹🇿 Tanzanie +255</option>
                          <option value="+66">🇹🇭 Thaïlande +66</option>
                          <option value="+670">🇹🇱 Timor oriental +670</option>
                          <option value="+228">🇹🇬 Togo +228</option>
                          <option value="+690">🇹🇰 Tokelau +690</option>
                          <option value="+676">🇹🇴 Tonga +676</option>
                          <option value="+1868">🇹🇹 Trinité-et-Tobago +1868</option>
                          <option value="+216">🇹🇳 Tunisie +216</option>
                          <option value="+90">🇹🇷 Turquie +90</option>
                          <option value="+993">🇹🇲 Turkménistan +993</option>
                          <option value="+1649">🇹🇨 Îles Turques-et-Caïques +1649</option>
                          <option value="+688">🇹🇻 Tuvalu +688</option>
                          <option value="+256">🇺🇬 Ouganda +256</option>
                          <option value="+380">🇺🇦 Ukraine +380</option>
                          <option value="+971">🇦🇪 Émirats arabes unis +971</option>
                          <option value="+44">🇬🇧 Royaume-Uni +44</option>
                          <option value="+1">🇺🇸 États-Unis +1</option>
                          <option value="+598">🇺🇾 Uruguay +598</option>
                          <option value="+998">🇺🇿 Ouzbékistan +998</option>
                          <option value="+678">🇻🇺 Vanuatu +678</option>
                          <option value="+379">🇻🇦 Vatican +379</option>
                          <option value="+58">🇻🇪 Venezuela +58</option>
                          <option value="+84">🇻🇳 Vietnam +84</option>
                          <option value="+1284">🇻🇬 Îles Vierges britanniques +1284</option>
                          <option value="+1340">🇻🇮 Îles Vierges américaines +1340</option>
                          <option value="+681">🇼🇫 Wallis-et-Futuna +681</option>
                          <option value="+967">🇾🇪 Yémen +967</option>
                          <option value="+260">🇿🇲 Zambie +260</option>
                          <option value="+263">🇿🇼 Zimbabwe +263</option>
                        </select>
                        <input
                          type="tel"
                          placeholder="4XX XX XX XX"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="flex-1 px-4 py-3 rounded-lg bg-white text-black placeholder-gray-500 border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base"
                          required
                          data-testid="reservation-phone-input"
                        />
                      </div>
                    </div>
                </div>
              </div>

              {/* 9. Notes */}
              <div>
                  <label className="block text-sm text-white font-semibold mb-2">Notes</label>
                  <textarea
                    placeholder={rt.notesPlaceholder}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows="3"
                    className="w-full px-4 py-3 rounded-lg bg-white text-black placeholder-gray-500 border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-base"
                    data-testid="reservation-notes-input"
                  ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 rounded-lg transition-colors text-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                data-testid="submit-reservation-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <LoadingIcon className="w-5 h-5" /> {rt.submitting}
                  </span>
                ) : priceInfo ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckIcon className="w-5 h-5" /> {rt.confirmButton} ({Math.round(priceInfo.price)}€)
                  </span>
                ) : (
                  rt.submitButton
                )}
              </button>
            </form>
          </div>
        </>
        )}
      </div>
    </div>
  );
};

const HeroSection = ({ lang, onReserveClick }) => {
  const t = translations[lang]?.hero || translations.fr.hero;

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900" data-testid="hero-section">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1920')] bg-cover bg-center opacity-20"></div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20 pb-10">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4" data-testid="hero-title">
          {t.title}
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-2" data-testid="hero-subtitle">
          {t.subtitle}
        </p>
        <p className="text-base sm:text-lg text-yellow-500 mb-4" data-testid="hero-description">
          {t.description}
        </p>
        <p className="text-xs sm:text-sm text-gray-400 mb-8 italic">
          {t.tags}
        </p>

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-4 sm:p-6 md:p-8 mt-8 sm:mt-12">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6" data-testid="reservation-title">{t.reservationTitle}</h2>
          <button
            onClick={onReserveClick}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 sm:py-5 rounded-lg transition-colors text-lg sm:text-xl"
            data-testid="reserve-button"
          >
            {t.reserveButton}
          </button>
          <p className="text-xs sm:text-sm text-gray-300 text-center mt-4" data-testid="features-text">
            {t.features}
          </p>
        </div>
      </div>
    </section>
  );
};

const WhyUsSection = ({ lang }) => {
  const t = translations[lang]?.whyUs || translations.fr.whyUs;

  const features = [
    { title: t.feature1Title, desc: t.feature1Desc },
    { title: t.feature2Title, desc: t.feature2Desc },
    { title: t.feature3Title, desc: t.feature3Desc },
    { title: t.feature4Title, desc: t.feature4Desc },
    { title: t.feature5Title, desc: t.feature5Desc },
    { title: t.feature6Title, desc: t.feature6Desc },
  ];

  return (
    <section id="why-us" className="py-12 sm:py-16 md:py-20 bg-gray-900" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-white mb-3 sm:mb-4" data-testid="why-us-title">
          {t.title}
        </h2>
        <p className="text-lg sm:text-xl text-gray-400 text-center mb-8 sm:mb-12 px-4">
          {t.subtitle}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {features.map((feature) => (
            <div key={feature.title} className="bg-gray-800 p-6 rounded-lg border-l-4 border-yellow-500">
              <h3 className="text-xl font-bold mb-2 text-white">{feature.title}</h3>
              <p className="text-gray-300">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DestinationsSection = ({ lang }) => {
  const t = translations[lang]?.destinations || translations.fr.destinations;

  const destinations = [
    { title: t.airport, desc: t.airportDesc },
    { title: t.cities, desc: t.citiesDesc },
    { title: t.europe, desc: t.europeDesc },
    { title: t.stations, desc: t.stationsDesc },
  ];

  return (
    <section id="destinations" className="py-12 sm:py-16 md:py-20 bg-black" data-testid="destinations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-white mb-3 sm:mb-4" data-testid="destinations-title">
          {t.title}
        </h2>
        <p className="text-lg sm:text-xl text-gray-400 text-center mb-8 sm:mb-12 px-4">
          {t.subtitle}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {destinations.map((dest) => (
            <div key={dest.title} className="bg-white p-6 rounded-lg shadow-md border-t-4 border-yellow-500">
              <h3 className="text-xl font-bold mb-2 text-gray-900">{dest.title}</h3>
              <p className="text-gray-600">{dest.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactSection = ({ lang }) => {
  const t = translations[lang]?.contact || translations.fr.contact;
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(`Message envoyé: ${formData.name}`);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <section id="contact" className="py-12 sm:py-16 md:py-20 bg-gray-900" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-white mb-3 sm:mb-4" data-testid="contact-title">
          {t.title}
        </h2>
        <p className="text-lg sm:text-xl text-gray-400 text-center mb-8 sm:mb-12 px-4">
          {t.subtitle}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-yellow-500 mb-2">{t.email}</h3>
              <p className="text-sm sm:text-base text-white break-all">info@brutaxi-bel.be</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-yellow-500 mb-2">{t.phone}</h3>
              <p className="text-sm sm:text-base text-white">02 844 54 87</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-yellow-500 mb-2">{t.address}</h3>
              <p className="text-sm sm:text-base text-white">{t.addressValue}</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-yellow-500 mb-2">{t.hours}</h3>
              <p className="text-sm sm:text-base text-white">{t.hoursValue}</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              placeholder={t.namePlaceholder}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-yellow-500 focus:outline-none text-sm sm:text-base"
              required
              data-testid="contact-name-input"
            />
            <input
              type="email"
              placeholder={t.emailPlaceholder}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-yellow-500 focus:outline-none text-sm sm:text-base"
              required
              data-testid="contact-email-input"
            />
            <textarea
              placeholder={t.messagePlaceholder}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows="6"
              className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-yellow-500 focus:outline-none text-sm sm:text-base"
              required
              data-testid="contact-message-input"
            ></textarea>
            <button
              type="submit"
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 rounded-lg transition-colors text-sm sm:text-base"
              data-testid="contact-submit-button"
            >
              {t.sendButton}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ lang }) => {
  const navigate = useNavigate();
  const t = translations[lang]?.footer || translations.fr.footer;
  const navT = translations[lang]?.nav || translations.fr.nav;

  const scrollToSection = (sectionId) => {
    if (window.location.pathname !== `/${lang}`) {
      navigate(`/${lang}`);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-black border-t border-gray-800 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div>
            <h3 className="text-2xl font-bold text-yellow-500 mb-4">BRUTAXI</h3>
            <p className="text-gray-400 mb-4">{t.description}</p>
            <p className="text-gray-500 text-sm">{t.company}</p>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">{t.quickLinks}</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-yellow-500 transition-colors">
                  {navT.home}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('destinations')} className="text-gray-400 hover:text-yellow-500 transition-colors">
                  {navT.destinations}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('why-us')} className="text-gray-400 hover:text-yellow-500 transition-colors">
                  {navT.whyUs}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-yellow-500 transition-colors">
                  {navT.contact}
                </button>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">{t.legal}</h4>
            <ul className="space-y-2">
              <li>
                <button className="text-gray-400 hover:text-yellow-500 transition-colors">
                  {t.legal}
                </button>
              </li>
              <li>
                <button className="text-gray-400 hover:text-yellow-500 transition-colors">
                  {t.privacy}
                </button>
              </li>
              <li>
                <button className="text-gray-400 hover:text-yellow-500 transition-colors">
                  {t.terms}
                </button>
              </li>
              <li>
                <a href="/admin" className="text-gray-400 hover:text-yellow-500 transition-colors">
                  Administration
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
          <p>{t.rights}</p>
        </div>
      </div>
    </footer>
  );
};

const LegacyHomePage = () => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const currentLang = lang && ['fr', 'nl', 'en'].includes(lang) ? lang : 'fr';
  const [showLangModal, setShowLangModal] = useState(() => !localStorage.getItem('brutaxi_visited'));
  const [showReservationModal, setShowReservationModal] = useState(false);

  useEffect(() => {
    if (!lang) {
      navigate('/fr', { replace: true });
    }
  }, [lang, navigate]);

  useEffect(() => {
    // Mark as visited (modal visibility computed via lazy initial state)
    if (!localStorage.getItem('brutaxi_visited')) {
      localStorage.setItem('brutaxi_visited', 'true');
    }
  }, []);

  useEffect(() => {
    // Handle initial hash in URL (scroll to section on page load)
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, []);

  const handleLanguageSelect = (newLang) => {
    navigate(`/${newLang}`);
    setShowLangModal(false);
  };

  const handleReserveClick = () => {
    setShowReservationModal(true);
  };

  return (
    <div className="min-h-screen bg-black">
      <Header lang={currentLang} onLangClick={() => setShowLangModal(true)} onReserveClick={handleReserveClick} />
      <HeroSection lang={currentLang} onReserveClick={handleReserveClick} />
      <WhyUsSection lang={currentLang} />
      <DestinationsSection lang={currentLang} />
      <ContactSection lang={currentLang} />
      <Footer lang={currentLang} />
      <LanguageModal
        isOpen={showLangModal}
        onClose={() => setShowLangModal(false)}
        onSelect={handleLanguageSelect}
        currentLang={currentLang}
      />
      <ReservationModal
        isOpen={showReservationModal}
        onClose={() => setShowReservationModal(false)}
        lang={currentLang}
      />
    </div>
  );
};

// Wrapper component for pages that need lang state
const PageWrapper = ({ children }) => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const [currentLang, setCurrentLang] = useState(lang || 'fr');
  const [showLangModal, setShowLangModal] = useState(() => !localStorage.getItem('brutaxi_visited'));
  const [showReservationModal, setShowReservationModal] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('brutaxi_visited')) {
      localStorage.setItem('brutaxi_visited', 'true');
    }
  }, []);

  const handleLanguageSelect = (newLang) => {
    setCurrentLang(newLang);
    setShowLangModal(false);
  };

  const handleReserveClick = () => {
    setShowReservationModal(true);
  };

  const setLang = (newLang) => {
    setCurrentLang(newLang);
  };

  // Clone children with props
  const childrenWithProps = typeof children === 'function' 
    ? children({ 
        lang: currentLang, 
        setLang, 
        onReserveClick: handleReserveClick,
        onLangClick: () => setShowLangModal(true)
      })
    : children;

  return (
    <>
      {childrenWithProps}
      <LanguageModal
        isOpen={showLangModal}
        onClose={() => setShowLangModal(false)}
        onSelect={handleLanguageSelect}
        currentLang={currentLang}
      />
      <ReservationModal
        isOpen={showReservationModal}
        onClose={() => setShowReservationModal(false)}
        lang={currentLang}
      />
    </>
  );
};

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-black">
    <LoadingIcon className="w-8 h-8 animate-spin text-yellow-500" />
  </div>
);

// Dedicated booking-form page (deep link: /fr/booking, /nl/booking, /en/booking)
const BookingPage = () => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const currentLang = ['fr', 'nl', 'en'].includes(lang) ? lang : 'fr';

  return (
    <div className="min-h-screen bg-black" data-testid="booking-page">
      <Header lang={currentLang} onReserveClick={() => {}} onLangClick={() => {}} />
      <ReservationModal
        isOpen={true}
        startWithForm={true}
        onClose={() => navigate(`/${currentLang}`)}
        lang={currentLang}
      />
    </div>
  );
};

function App() {
  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      libraries={libraries}
    >
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Home routes */}
            <Route path="/" element={<LegacyHomePage />} />
            <Route path="/:lang" element={<LegacyHomePage />} />
            
            {/* Admin */}
            <Route path="/admin" element={<SimpleAdminDashboard />} />

            {/* Dedicated booking form page (fr/nl/en) */}
            <Route path="/:lang/booking" element={<BookingPage />} />
            
            {/* Multi-page SEO routes */}
            <Route path="/pourquoi-nous" element={
              <PageWrapper>
                {({ lang, setLang, onReserveClick }) => (
                  <Suspense fallback={<LoadingFallback />}>
                    <WhyUsPage lang={lang} setLang={setLang} onReserveClick={onReserveClick} />
                  </Suspense>
                )}
              </PageWrapper>
            } />
            <Route path="/destinations" element={
              <PageWrapper>
                {({ lang, setLang, onReserveClick }) => (
                  <Suspense fallback={<LoadingFallback />}>
                    <DestinationsPage lang={lang} setLang={setLang} onReserveClick={onReserveClick} />
                  </Suspense>
                )}
              </PageWrapper>
            } />
            <Route path="/contact" element={
              <PageWrapper>
                {({ lang, setLang, onReserveClick }) => (
                  <Suspense fallback={<LoadingFallback />}>
                    <ContactPage lang={lang} setLang={setLang} onReserveClick={onReserveClick} />
                  </Suspense>
                )}
              </PageWrapper>
            } />
            <Route path="/aeroports" element={
              <PageWrapper>
                {({ lang, setLang, onReserveClick }) => (
                  <Suspense fallback={<LoadingFallback />}>
                    <AirportsPage lang={lang} setLang={setLang} onReserveClick={onReserveClick} />
                  </Suspense>
                )}
              </PageWrapper>
            } />
            
            {/* Thank you pages */}
            <Route path="/fr/merci" element={
              <Suspense fallback={<LoadingFallback />}>
                <ThankYouPage />
              </Suspense>
            } />
            <Route path="/nl/bedankt" element={
              <Suspense fallback={<LoadingFallback />}>
                <ThankYouPage />
              </Suspense>
            } />
            <Route path="/en/thank-you" element={
              <Suspense fallback={<LoadingFallback />}>
                <ThankYouPage />
              </Suspense>
            } />
          </Routes>
        </BrowserRouter>
      </div>
    </LoadScript>
  );
}

export default App;